import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { pipelineApi } from '../api';
import PidCanvas from '../components/PidCanvas';
import type {
  CanvasInspectionOverlay,
  PipelineInspectionBranch,
  PipelineInspectionManifest,
  PipelineInspectionStage,
} from '../types/pipelineInspection';
import './PipelineInspectionPage.css';

const BRANCH_ORDER: PipelineInspectionBranch[] = ['common', 'spatial', 'semantic'];

function stageStatusLabel(stage: PipelineInspectionStage): string {
  if (stage.status === 'unavailable') return 'Not captured';
  if (stage.health === 'warning') return 'Review';
  if (stage.health === 'error') return 'Failed';
  return 'Available';
}

function toCanvasOverlay(stage: PipelineInspectionStage | undefined): CanvasInspectionOverlay | null {
  const overlay = stage?.overlay;
  if (!overlay) return null;
  return {
    footprint: overlay.footprint,
    dimOutsideFootprint: overlay.dim_outside_footprint,
    tiles: overlay.tiles,
    textBoxes: overlay.text_boxes,
    equipmentBoxes: overlay.equipment_boxes,
    lineBoxes: overlay.line_boxes,
  };
}

const PipelineInspectionPage: React.FC = () => {
  const { runId = '' } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<PipelineInspectionManifest | null>(null);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [focusEvidence, setFocusEvidence] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    pipelineApi
      .getRunInspection(runId, {
        bucket: searchParams.get('bucket') || undefined,
        pageNum: Number(searchParams.get('page') || 0),
      })
      .then((result) => {
        if (cancelled) return;
        setManifest(result);
        setSelectedStageId(result.earliest_loss?.stage_id || result.stages[0]?.id || '');
      })
      .catch((requestError: unknown) => {
        if (cancelled) return;
        const message = requestError instanceof Error ? requestError.message : 'Could not load run inspection.';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, searchParams]);

  const selectedStage = useMemo(
    () => manifest?.stages.find((stage) => stage.id === selectedStageId),
    [manifest, selectedStageId],
  );
  const canvasOverlay = useMemo(() => toCanvasOverlay(selectedStage), [selectedStage]);

  const groupedStages = useMemo(() => {
    const groups = new Map<PipelineInspectionBranch, PipelineInspectionStage[]>();
    BRANCH_ORDER.forEach((branch) => groups.set(branch, []));
    manifest?.stages.forEach((stage) => groups.get(stage.branch)?.push(stage));
    return groups;
  }, [manifest]);

  const selectStage = useCallback((stage: PipelineInspectionStage) => {
    setSelectedStageId(stage.id);
    if (!stage.overlay?.footprint) setFocusEvidence(false);
  }, []);

  if (loading) {
    return <main className="inspection-state">Loading run inspection...</main>;
  }

  if (error || !manifest || !selectedStage) {
    return (
      <main className="inspection-state inspection-state--error">
        <h1>Run inspection unavailable</h1>
        <p>{error || 'This run does not expose compatible inspection artifacts.'}</p>
        <button type="button" onClick={() => navigate(-1)}>Go back</button>
      </main>
    );
  }

  const commonStages = groupedStages.get('common') || [];
  const beforeBranches = commonStages.filter((stage) => stage.id === 'discovery' || stage.id === 'source');
  const afterBranches = commonStages.filter((stage) => stage.id !== 'discovery' && stage.id !== 'source');
  const evidenceFootprint = focusEvidence ? selectedStage.overlay?.footprint || null : null;
  // Each stage's footprint is one navigation, so a re-render does not frame it again.
  const focusRequest = evidenceFootprint ? { bbox: evidenceFootprint, requestId: selectedStage.id } : null;

  return (
    <main className="inspection-page">
      <header className="inspection-header">
        <div className="inspection-heading">
          <button className="inspection-back" type="button" onClick={() => navigate(`/pnid/${manifest.doc_id}`)}>
            ← Document
          </button>
          <div>
            <div className="inspection-kicker">Run inspection</div>
            <h1>{runId}</h1>
          </div>
        </div>
        <div className="inspection-run-state">
          <span className={`inspection-dot inspection-dot--${manifest.trace_health === 'degraded' ? 'warning' : 'ok'}`} />
          <span>{manifest.run_status || 'Unknown status'}</span>
          <span className="inspection-separator">·</span>
          <span>{manifest.trace_health === 'degraded' ? 'Degraded evidence' : 'Healthy evidence'}</span>
        </div>
      </header>

      {manifest.earliest_loss && (
        <section className="inspection-loss" aria-label="Earliest material loss">
          <div>
            <strong>Earliest material loss</strong>
            <p>{manifest.earliest_loss.message}</p>
          </div>
          <span>{manifest.earliest_loss.basis}</span>
        </section>
      )}

      <div className="inspection-workspace">
        <aside className="inspection-stages" aria-label="Pipeline stages">
          <h2>Execution path</h2>
          <div className="inspection-stage-list">
            {beforeBranches.map((stage) => (
              <StageButton key={stage.id} stage={stage} selected={stage.id === selectedStageId} onSelect={selectStage} />
            ))}
          </div>

          <div className="inspection-branch">
            <h3>Spatial branch</h3>
            {(groupedStages.get('spatial') || []).map((stage) => (
              <StageButton key={stage.id} stage={stage} selected={stage.id === selectedStageId} onSelect={selectStage} />
            ))}
          </div>

          <div className="inspection-branch">
            <h3>Semantic branch</h3>
            {(groupedStages.get('semantic') || []).map((stage) => (
              <StageButton key={stage.id} stage={stage} selected={stage.id === selectedStageId} onSelect={selectStage} />
            ))}
          </div>

          <div className="inspection-stage-list inspection-stage-list--joined">
            {afterBranches.map((stage) => (
              <StageButton key={stage.id} stage={stage} selected={stage.id === selectedStageId} onSelect={selectStage} />
            ))}
          </div>
        </aside>

        <section className="inspection-canvas-region" aria-label="Drawing evidence">
          <div className="inspection-canvas-toolbar">
            <div className="inspection-legend" aria-label="Overlay legend">
              <span><i className="inspection-swatch inspection-swatch--crop" />Selected footprint</span>
              <span><i className="inspection-swatch inspection-swatch--ocr" />OCR</span>
              <span><i className="inspection-swatch inspection-swatch--equipment" />Equipment</span>
              <span><i className="inspection-swatch inspection-swatch--line" />Line</span>
            </div>
            {selectedStage.overlay?.footprint && (
              <div className="inspection-view-toggle" role="group" aria-label="Drawing view">
                <button
                  type="button"
                  aria-pressed={!focusEvidence}
                  onClick={() => setFocusEvidence(false)}
                >
                  Full sheet
                </button>
                <button
                  type="button"
                  aria-pressed={focusEvidence}
                  onClick={() => setFocusEvidence(true)}
                >
                  Focus evidence
                </button>
              </div>
            )}
          </div>
          <div className="inspection-canvas">
            <PidCanvas
              key={manifest.run_id}
              imageUrl=""
              docId={manifest.doc_id}
              logicalImageSize={{ width: manifest.source_frame.width, height: manifest.source_frame.height }}
              inspectionOverlay={canvasOverlay}
              hidePerfHud
              // This viewer frames the drawing itself through `focusRequest`. A
              // cursor arriving from either sidebar would scroll that framing
              // away before it reached the canvas.
              edgePanEnabled={false}
              fitOnFocusClear
              focusRequest={focusRequest}
              sections={[]}
              pendingPieces={[]}
              onBoxDrawn={() => undefined}
              onLineDrawn={() => undefined}
              onSelectSection={() => undefined}
              readOnly
            />
          </div>
        </section>

        <aside className="inspection-evidence" aria-label="Stage evidence">
          <div className="inspection-evidence-heading">
            <div>
              <div className="inspection-kicker">{selectedStage.branch} stage</div>
              <h2>{selectedStage.label}</h2>
            </div>
            <span className={`inspection-badge inspection-badge--${selectedStage.health}`}>
              {stageStatusLabel(selectedStage)}
            </span>
          </div>

          <dl className="inspection-metrics">
            {selectedStage.metrics.map((metric) => (
              <div key={`${metric.label}-${metric.value}`}>
                <dt>{metric.label}</dt>
                <dd>{metric.value}</dd>
              </div>
            ))}
          </dl>

          <EvidenceBlock label="Input" value={selectedStage.input_summary} />
          <EvidenceBlock label="Output" value={selectedStage.output_summary} />
          <EvidenceBlock label="Why it matters" value={selectedStage.impact_summary} />

          {selectedStage.trace_gap && (
            <section className="inspection-trace-gap">
              <strong>Trace gap</strong>
              <p>{selectedStage.trace_gap}</p>
            </section>
          )}

          <footer>
            <span>Coordinate frame</span>
            <code>{manifest.source_frame.coordinate_space}</code>
          </footer>
        </aside>
      </div>
    </main>
  );
};

const StageButton: React.FC<{
  stage: PipelineInspectionStage;
  selected: boolean;
  onSelect: (stage: PipelineInspectionStage) => void;
}> = ({ stage, selected, onSelect }) => (
  <button
    className="inspection-stage"
    type="button"
    aria-current={selected ? 'step' : undefined}
    onClick={() => onSelect(stage)}
  >
    <span className={`inspection-dot inspection-dot--${stage.health}`} />
    <span>
      <strong>{stage.label}</strong>
      <small>{stageStatusLabel(stage)}</small>
    </span>
  </button>
);

const EvidenceBlock: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <section className="inspection-evidence-block">
    <h3>{label}</h3>
    <p>{value}</p>
  </section>
);

export default PipelineInspectionPage;
